import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

/**
 * Tumbling reels — one tumble step. The reel reveal stops on a 5-cell L1 cluster
 * (bottom-left); it highlights, the winners are REMOVED by mask, then the
 * `collapse` event drives the gravity drop-in: survivors above the gaps fall,
 * new symbols drop in from the top. No further win on the collapse, so the round
 * ends (one step). Game-A-style cluster/mask win — no payline.
 *
 * Drives the OPT-IN tumble path (TumbleMechanic + TumbleReelGridPresenter +
 * buildTumbleReelsReplayPlugin). Authoring order matters: grid_win_highlight
 * BEFORE remove_symbols_by_mask, both in the reveal event; the collapse event
 * carries the post-tumble grid.
 */
export function baseFixtureReelTumble(): SpinResponse {
  // L1 cluster at indices 10,11 (row2 col0,1) + 15,16,17 (row3 col0,1,2).
  const winIndices = [10, 11, 15, 16, 17];
  const winMask = Array.from({ length: 20 }, (_, i) => (winIndices.includes(i) ? "1" : "0")).join("");
  const amountMinor = "500";

  return spinFixture("reel-tumble")
    .balance("EUR", "100000")
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter(amountMinor).gridRows([
        ["L4", "L5", "L2", "H1", "L3"],
        ["L3", "L4", "L5", "L2", "L5"],
        ["L1", "L1", "L4", "L5", "L2"],
        ["L1", "L1", "L1", "L3", "L4"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const w = fx.gridWinHighlight({
        positionMask: winMask,
        winAmount: amountMinor, // 250 × 2
        symbolIdentifier: "L1",
        symbolCount: "5",
        lines: [{ positionMask: winMask, winAmount: amountMinor, baseWinAmount: "250", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win" });

      // Cannon fires x2 on this substep's win.
      const fire = cannonFire(0);
      e.action(fire.type, fire.payload, { actionPhaseName: fire.phase, debugTag: "cannon_fire" });

      const r = fx.removeSymbolsByMask(winMask);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove" });

      const t = fx.updateTotalWinMeter(amountMinor);
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total" });
    })
    .event("collapse", 1, (e) => {
      // Post-tumble grid = EXACT gravity result of (reveal grid − win mask), so the survivors keep
      // their identity and merely FALL (the mechanic rebinds any survivor whose id disagrees with
      // this model, which would read as symbols morphing mid-tumble). Cols 0,1 lost 2 cells each →
      // their 2 survivors land on rows 2-3 with 2 fresh symbols dropping in above; col 2 lost 1 →
      // its 3 survivors shift down one, 1 drops in; cols 3,4 untouched.
      e.reelSet("default").betAmount("100").accumulatedWinAfter(amountMinor).gridRows([
        ["L2", "L3", "H1", "H1", "L3"],
        ["L4", "L5", "L2", "L2", "L5"],
        ["L4", "L5", "L5", "L5", "L2"],
        ["L3", "L4", "L4", "L3", "L4"],
      ]);

      const end = fx.spinBoundaryEnd("base:1", amountMinor);
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
